import React, { useState } from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { TertiaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import { Body } from '@grnhse/seedling/lib/birch/typography';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import { withLocalePrefix } from 'shared/utils/translation';
import PopoverButton from 'talent_matching/components/filters/popover_button';
import MilestoneDropdown from 'talent_search/components/dropdowns/milestone_dropdown';
import StagesDropdown from 'talent_search/components/dropdowns/stages_dropdown';
import RejectionReasonsDropdown from 'talent_matching/talent_rediscovery/components/filters/rejection_reasons_dropdown';
import ScorecardPerformanceDropdown from 'talent_search/components/dropdowns/scorecard_performance_dropdown';
import ScorecardAttributesSection from 'talent_matching/talent_rediscovery/components/filters/scorecard_attributes_section'; // eslint-disable-line max-len
import { useRediscoveryTabContext } from 'talent_matching/contexts/rediscovery_tab_context';
import { SCORECARD_ATTRIBUTE_MATCH } from 'talent_search/shared/types';

const t = withLocalePrefix('talent_matching.rediscovery.filter_row');

const REDISCOVERY_PAGE_NAME = 'rediscovery' as const;

const QualityFiltersGroup = () => {
  const { apiClient, milestones, focusAttributes } = useRediscoveryTabContext();
  const { watch, setValue } = useFormContext();
  const [
    milestone,
    stages,
    includeRejectionReasons,
    scorecardPerformance,
    noStrongNo,
    excludeHired,
    scorecardAttributeFilters,
  ] = watch([
    'milestone',
    'stages',
    'include_rejection_reasons',
    'scorecard_performance',
    'no_strong_no_scores',
    'exclude_hired',
    'scorecard_attribute_filters',
  ]);

  const hasMilestone = !!milestone;
  const hasStages = Array.isArray(stages) && stages.length > 0;
  const hasIncludeRejectionReasons =
    Array.isArray(includeRejectionReasons) &&
    includeRejectionReasons.length > 0;
  const hasScorecard = scorecardPerformance !== null;
  const hasNoStrongNo = Boolean(noStrongNo);
  const hasExcludeHired = Boolean(excludeHired);
  const hasAttributeFilters =
    Array.isArray(scorecardAttributeFilters) &&
    scorecardAttributeFilters.length > 0;

  const [showStages, setShowStages] = useState(hasStages);

  const appliedCount = [
    hasMilestone,
    hasStages,
    hasIncludeRejectionReasons,
    hasScorecard,
    hasNoStrongNo,
    hasExcludeHired,
    hasAttributeFilters,
  ].filter(Boolean).length;

  const clear = () => {
    setValue('milestone', null, { shouldDirty: true });
    setValue('stages', [], { shouldDirty: true });
    setValue('include_rejection_reasons', [], { shouldDirty: true });
    setValue('scorecard_performance', null, { shouldDirty: true });
    setValue('no_strong_no_scores', false, { shouldDirty: true });
    setValue('exclude_hired', false, { shouldDirty: true });
    setValue('scorecard_attribute_filters', [], { shouldDirty: true });
    setValue('scorecard_attribute_match', SCORECARD_ATTRIBUTE_MATCH.ANY, {
      shouldDirty: true,
    });
  };

  const buttonLabel = `${t('quality.button')}${
    appliedCount ? ` (${appliedCount})` : ''
  }`;

  return (
    <PopoverButton
      buttonLabel={buttonLabel}
      title={t('quality.title')}
      width="600px"
    >
      <Stack spacing={3}>
        <Body>{t('quality.description')}</Body>
        <Stack spacing={2}>
          <Body medium>{t('quality.how_far_header')}</Body>
          <Stack spacing={1}>
            <MilestoneDropdown
              milestones={milestones}
              pageName={REDISCOVERY_PAGE_NAME}
              helperText={t('quality.milestone_helper_test')}
            />
            {showStages && (
              <StagesDropdown
                apiClient={apiClient}
                pageName={REDISCOVERY_PAGE_NAME}
              />
            )}
            <Stack alignItems="flex-start" sx={{ marginLeft: 1.5 }}>
              <TertiaryButton
                onClick={() => setShowStages(!showStages)}
                size="md"
              >
                {showStages
                  ? t('quality.hide_stages')
                  : t('quality.show_stages')}
              </TertiaryButton>
            </Stack>
          </Stack>

          <RejectionReasonsDropdown
            fetchRejectionReasons={apiClient.fetchRejectionReasons}
            type="include"
            helperText={t('quality.include_rejection_reasons_helper_text')}
          />
        </Stack>

        <Stack spacing={2}>
          <Body medium>{t('quality.interview_performance_header')}</Body>
          <ScorecardPerformanceDropdown
            pageName={REDISCOVERY_PAGE_NAME}
            helperText={t('quality.scorecard_performance_helper_text')}
            hideCheckboxes
          />
          <ScorecardAttributesSection
            fetchScorecardAttributes={apiClient.fetchScorecardAttributes}
            focusAttributes={focusAttributes}
          />
        </Stack>

        <Stack sx={{ alignSelf: 'flex-start' }}>
          <TertiaryButton onClick={clear} size="lg">
            {t('clear')}
          </TertiaryButton>
        </Stack>
      </Stack>
    </PopoverButton>
  );
};

export default QualityFiltersGroup;
